import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from './pages/login.tsx';
import NotFound from './pages/player/404.tsx';
import Layout from './pages/player/Layout.tsx';
import TablePendingBetsComponent from './components/player/pendig/TablePendingBetsComponent.tsx';
import UpdateTableComponent from './components/player/updateUser/UpdateTableComponent.tsx';
import LayoutUser from './pages/player/LayoutUser.tsx';
import HistoryLayoutComponent from './components/player/userHistory/HistoryLayoutComponent.tsx';
import DayCalendarComponent from './components/player/userHistory/DayCalendarComponent.tsx';
import WeekCalendarComponent from './components/player/userHistory/WeekCalendarComponent.tsx';
import MonthComponent from './components/player/userHistory/MonthComponent.tsx';
import YearCalendarComponent from './components/player/userHistory/YearCalendarComponent.tsx';
import PasswordRecovery from './pages/player/PasswordRecovery.tsx';
import FrequentQuestions from './pages/player/FrequentQuestions.tsx';
import RulesBox from './pages/player/RulesBox.tsx';
import UserAccount from './pages/player/UserAccount.tsx';
import LayoutMessaging from './pages/player/LayoutMessaging.tsx';
import { useUser } from './context/UserContext.tsx';
import ParlayComponents from './components/player/typeGame/parlay/ParlayComponents.tsx';
import TeaserComponents from './components/player/typeGame/teaser/TeaserComponents.tsx';
import Figures from './components/player/userHistory/Figures.tsx';
import { HomeContent } from './components/player/HomeContent.tsx';
import GamesMenuProps from './components/player/typeGame/GamesProps/GamesMenuProps.tsx';
import LegueProps from './components/player/typeGame/leagueProps/LeagueProps.tsx';
import { FrequentQuestionsAdmin } from './pages/admin/FrequentQuestions.tsx';
import { LayoutUserAdmin } from './pages/admin/LayoutUser.tsx';
import { LayoutMessagingAdmin } from './pages/admin/LayoutMessaging.tsx';
import LayoutAgentAdmin from './pages/admin/LayoutAgentAdmin.tsx';
import LayoutAdmin from './pages/admin/Layout.tsx';
import UserBasics from './components/admin/user/TheBasic/UserBasics.tsx';
import UserTransaction from './components/admin/user/userTransaction/UserTransaction.tsx';
import UserPending from './components/admin/user/userPending/UserPending.tsx';
import UserFreePlay from './components/admin/user/freePlay/UserFreePlay.tsx';
import UserCasinoBonus from './components/admin/user/casinoBonus/UserCasinoBonus.tsx';
import RegisterSubAgent from './pages/admin/RegisterSubAgent.tsx';
import RegisterPlayer from './pages/admin/RegisterPlayer.tsx';
import Weekly from './components/admin/weekly-user/Weekly.tsx';
import CustomerAdmin from './components/admin/customerAdmin/CustomerAdmin.tsx';
import UsersPending from './components/admin/pending/UsersPending.tsx';
import AgentManagement from './components/admin/agentAdmin/AgentManagement.tsx';
import GameAdmin from './components/admin/gameAdmin/GameAdmin.tsx';
import Billing from './components/admin/Billing.tsx';
import TransactionBody from './components/admin/transactionsHistory/TransactionBody.tsx';
import DeletedWagers from './components/admin/deletedWagers/DeletedWagers.tsx';
import UserIP from './components/admin/userIP/UserIP.tsx';
import Performance from './components/admin/user/performance/Performance.tsx';
import LayoutComponent from './pages/admin/LayoutComponent.tsx';
import { ModalProvider } from './context/ModalContext.tsx';
import CommercialLive from './components/player/typeGame/commercial/CommercialLive.tsx';
import CommercialComponent from './pages/player/LiveComponent.tsx';
import VimplayGames from './pages/player/VimplayGames.tsx';
import { VIMPLAY_GAMES_ENABLED, canAccessAdminCasino } from './services/player/VimplayService.tsx';
import { FaSyncAlt } from "react-icons/fa";
import BettingSetup from "./components/admin/bettingsetup/BettingSetup.tsx";
import ClosingLineAnalysis from "./components/admin/analytics/ClosingLineAnalysis.tsx";
import ClosingLineDetail from "./components/admin/user/closingLine/ClosingLineDetail.tsx";
import { CLOSING_LINE_ANALYTICS_ENABLED } from "./helpers/closingLineAnalysis.ts";
import SessionResumeGuard from "./components/SessionResumeGuard.tsx";
import PageLifecycleHandler from "./components/PageLifecycleHandler.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import {
    hasBrowserSessionAlive,
    isSessionExpiredAck,
    isSessionExpiredPending,
    isVoluntaryLogout,
} from "./helpers/browserSession.ts";

const hasStoredUser = () => {
    try {
        return Boolean(localStorage.getItem("user"));
    } catch {
        return false;
    }
};

/**
 * Component for defining protected routes and managing navigation within the application.
 * Uses BrowserRouter to enable routing functionality.
 */
const RoleProtectedRoute = ({ element, roles }) => {
    const { user, firebaseUser, isLoading } = useUser();

    // Closed-browser resume on a protected URL: bounce to login.
    // Skip once the session was already cleared / user is signing in again.
    // Check stored user too — do not wait for Firebase to restore before leaving the blank shell.
    if (
        !hasBrowserSessionAlive() &&
        !isVoluntaryLogout() &&
        !isSessionExpiredAck() &&
        (isSessionExpiredPending() || Boolean(firebaseUser) || hasStoredUser())
    ) {
        return <Navigate to="/" replace />;
    }

    if (isLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <FaSyncAlt className="loader-icon-proyect" />
        </div>;
    }
    if (!firebaseUser || !user || (roles?.length && !roles.includes(user.userType))) {
        return <Navigate to="/" replace />;
    }

    return element;
};

const CasinoAdminRoute = () => {
    const { user } = useUser();
    if (!canAccessAdminCasino(user)) {
        return <Navigate to="/homeAdmin" replace />;
    }
    return <UserCasinoBonus />;
};

const RoleAwareFallback = () => {
    const { user, firebaseUser, isLoading } = useUser();

    if (isLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <FaSyncAlt className="loader-icon-proyect" />
        </div>;
    }

    if (!firebaseUser || !user) {
        return <Navigate to="/" replace />;
    }

    if (user.userType === 'Player') {
        return <Navigate to="/home" replace />;
    }

    if (user.userType === 'masterAgent' || user.userType === 'subAgent') {
        return <Navigate to="/homeAdmin" replace />;
    }

    return <Navigate to="/" replace />;
};

const ProtectRoutes = () => {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <ModalProvider>
                <PageLifecycleHandler />
                <SessionResumeGuard />
                <Routes>
                    <Route path="/" element={<Login />} />
                    <Route path="/pass" element={<PasswordRecovery />} />
                    <Route path="/home" element={<RoleProtectedRoute element={<Layout />} roles={['Player']} />}>
                        <Route index element={<HomeContent />} />
                        <Route path="parlay" element={<ParlayComponents />} />
                        <Route path="teaser" element={<TeaserComponents />} />
                        <Route path="props/" element={<GamesMenuProps />} />
                        <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route path="/commercialLive" element={<RoleProtectedRoute element={<CommercialComponent />} roles={['Player']} />}>
                        <Route index element={<CommercialLive />} />
                        <Route path="props/" element={<GamesMenuProps />} />

                    </Route>
                    {VIMPLAY_GAMES_ENABLED && (
                        <>
                            <Route path="/vimplay" element={<RoleProtectedRoute element={<VimplayGames />} roles={['Player']} />} />
                            <Route path="/games" element={<Navigate to="/vimplay" replace />} />
                        </>
                    )}
                    <Route path="propsLeagues" element={<LegueProps />} />
                    <Route path="/FAQ" element={<RoleProtectedRoute element={<FrequentQuestions />} roles={['Player']} />} />
                    <Route path="/rulesBox" element={<RoleProtectedRoute element={<RulesBox />} roles={['Player']} />} />
                    {/* <Route path="/live" element={<RoleProtectedRoute element={<LiveComponent />} roles={['Player']} />} /> */}
                    <Route path="/account" element={<RoleProtectedRoute element={<UserAccount />} roles={['Player']} />} />
                    <Route path="/messaging" element={<RoleProtectedRoute element={<LayoutMessaging />} roles={['Player']} />} />
                    <Route path='/bets' element={<RoleProtectedRoute element={<LayoutUser />} roles={['Player']} />}>
                        <Route index element={<TablePendingBetsComponent />} />
                        <Route path='calendar' element={<HistoryLayoutComponent />}>
                            <Route index element={<MonthComponent />} />
                            <Route path="day/:date?" element={<DayCalendarComponent />} />
                            <Route path='week' element={<WeekCalendarComponent />} />
                            <Route path='month' element={<MonthComponent />} />
                            <Route path='year' element={<YearCalendarComponent />} />
                        </Route>
                        <Route path='update' element={<UpdateTableComponent />} />
                        <Route path='figures' element={<Figures />} />

                    </Route>

                    {/*admin — home is /homeAdmin only (no /home overlap with player) */}

                    <Route element={<RoleProtectedRoute element={<LayoutComponent />} roles={['subAgent', 'masterAgent']} />} >

                        <Route path="/admin/home" element={<Navigate to="/homeAdmin" replace />} />
                        <Route path="/weekly-figures" element={<Weekly />} />
                        <Route path="/frequentQuestions" element={<FrequentQuestionsAdmin />} />
                        <Route path="/customerAdmin" element={<CustomerAdmin />} />
                        <Route path="/pendingBets" element={<UsersPending />} />
                        <Route path="/gameAdmin" element={<GameAdmin />} />
                        <Route path="/add-player" element={<RegisterPlayer />} />
                        <Route path="/deleteWagers" element={<DeletedWagers />} />
                        <Route path="/billing" element={<Billing />} />
                        <Route path="/transactionHistory" element={<TransactionBody />} />
                        <Route path="/userIp" element={<UserIP />} />
                        <Route path="/register-an-agent" element={<RoleProtectedRoute element={<RegisterSubAgent />} roles={['masterAgent']} />} />
                        <Route path="/bettingSetup" element={<BettingSetup />} />
                        <Route path="/casino" element={<CasinoAdminRoute />} />
                        <Route
                            path="/analytics"
                            element={
                                CLOSING_LINE_ANALYTICS_ENABLED
                                    ? <ClosingLineAnalysis />
                                    : <Navigate to="/homeAdmin" replace />
                            }
                        />

                    </Route>

                    <Route path="/homeAdmin" element={<RoleProtectedRoute element={<LayoutAdmin />} roles={['subAgent', 'masterAgent']} />} />
                    <Route path="/messagingAdmin" element={<RoleProtectedRoute element={<LayoutMessagingAdmin />} roles={['subAgent', 'masterAgent']} />} />
                    <Route path="/user/:user" element={<RoleProtectedRoute element={<LayoutUserAdmin />} roles={['subAgent', 'masterAgent']} />}>
                        <Route index element={<UserBasics />} />
                        <Route path="basics" element={<UserBasics />} />
                        <Route path="transaction" element={<UserTransaction />} />
                        <Route path="pending" element={<UserPending />} />
                        <Route path="freePlays" element={<UserFreePlay />} />
                        <Route path="casinoBonus" element={<Navigate to="/casino" replace />} />
                        <Route path="performance" element={<Performance />} />
                        <Route
                            path="closingLine"
                            element={
                                CLOSING_LINE_ANALYTICS_ENABLED
                                    ? <ClosingLineDetail />
                                    : <Navigate to="basics" replace />
                            }
                        />
                    </Route>

                    <Route path="/manager" element={<RoleProtectedRoute element={<LayoutAgentAdmin />} roles={['masterAgent']} />}>
                        <Route index element={<AgentManagement />} />
                        <Route path="manager" element={<AgentManagement />} />
                    </Route>
                    <Route path="*" element={<RoleAwareFallback />} />
                </Routes>
                {/* <InactivityTimer /> */}

            </ModalProvider>
        </BrowserRouter>
    );
}
export default ProtectRoutes;




